import { keepPreviousData, useQuery } from "@tanstack/react-query";
import { useEffect, useState } from "react";
import { useParams } from "react-router-dom";

import { KeywordColumnSort } from "~/components/keywordList/KeywordTable";
import { PAGE_TITLES } from "~/configs/pageTitles";
import { usePageTitle } from "~/hooks/usePageTitle";

import { Keyword, Regions } from "../../api/Api";
import { apiClient } from "../../api/ApiClient";
import { KeywordList } from "../../components/keywordList/KeywordList";
import { PaginationConfig } from "../../components/ui/pagination/Pagination";
import { useSiteContext } from "../../contexts/SiteContext";
import styles from "./KeywordsPage.module.scss";

function useDebounce<T>(value: T, delay: number): T {
  const [debouncedValue, setDebouncedValue] = useState<T>(value);

  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedValue(value);
    }, delay);

    return () => {
      clearTimeout(timer);
    };
  }, [value, delay]);

  return debouncedValue;
}

const KeywordsPage = () => {
  usePageTitle(PAGE_TITLES.keywords);

  const { id } = useParams();
  const siteContext = useSiteContext();
  const site = siteContext?.site?.site;
  const [searchTerm, setSearchTerm] = useState("");
  const debouncedSearchTerm = useDebounce(searchTerm, 1000);
  const [sorting, setSorting] = useState<KeywordColumnSort[]>([
    { id: "now_pos", desc: false },
  ]);

  const focusKeywordId = site?.focus_keyword_id;

  const [pagination, setPagination] = useState<PaginationConfig>({
    currentPage: 1,
    itemsPerPage: 10,
    nextPage: null,
    prevPage: null,
    totalPages: 1,
    totalItems: 10,
  });

  const { data: regions } = useQuery({
    queryKey: ["regionsList"],
    queryFn: async () => {
      const response = await apiClient.sites.regionsList();

      return response?.data.regions;
    },
    staleTime: 1000 * 60 * 30,
  });

  const { data: focusKeywordDetail, isLoading: isLoadingFocusKeywordDetail } =
    useQuery({
      queryKey: ["keywordFocus", id, focusKeywordId],
      queryFn: async () => {
        if (!focusKeywordId) {
          return null;
        }
        const response = await apiClient.sites.keywordsDetail2(
          Number(id),
          focusKeywordId,
        );

        return response?.data?.keyword;
      },
      enabled: !!id && !!focusKeywordId,
    });

  const {
    data: keywords,
    isLoading: isKeywordsLoading,
    isFetching: isKeywordsFetching,
    refetch: refetchKeywordsList,
  } = useQuery({
    queryKey: [
      "keywordsList",
      id,
      debouncedSearchTerm,
      pagination.currentPage,
      pagination.itemsPerPage,
      toApiSorting(sorting),
    ],
    // Keep the previous page's rows visible during pagination / sort /
    // search refetches so the table doesn't blank-and-jump. The new
    // data atomically replaces the old once it lands. Only the very
    // first load (no previous data) hits the empty state.
    placeholderData: keepPreviousData,

    queryFn: async () => {
      const response = await apiClient.sites.keywordsDetail(
        Number(id),
        {
          "page[size]": pagination.itemsPerPage,
          "page[number]": pagination.currentPage,
          s: toApiSorting(sorting),
          q: {
            // @ts-expect-error BE types - missing input field
            name_cont: debouncedSearchTerm,
          },
        },
        {
          headers: {
            "Authorize-Member-Actions": "destroy,update,refresh,focus",
            "Authorize-Collection-Actions":
              "create,destroy,update,refresh,focus",
          },
        },
      );
      const { headers } = response;

      setPagination((prev: PaginationConfig) => ({
        ...prev,
        totalItems: Number(headers.get("x-pager-total-count")),
        nextPage: Number(headers.get("x-pager-next-page")),
        prevPage: Number(headers.get("x-pager-prev-page")),
        totalPages: Number(headers.get("x-pager-total-pages")),
      }));

      return {
        keywords: response?.data?.keywords,
        // @ts-expect-error BE response type is not correct intended
        authorizations: response?.data?.authorizations,
      };
    },
    enabled: Boolean(
      id && debouncedSearchTerm !== null && debouncedSearchTerm !== undefined,
    ),
  });

  const handleSearchChange = (term: string) => {
    setSearchTerm(term);
    if (pagination.currentPage !== 1) {
      setPagination((prev) => ({
        ...prev,
        currentPage: 1,
      }));
    }
  };

  return (
    <div className={styles.page}>
      <h1 className={styles.title}>{PAGE_TITLES.keywordsList}</h1>
      <KeywordList
        site={site}
        refetch={refetchKeywordsList}
        searchTerm={searchTerm}
        setSearchTerm={handleSearchChange}
        keywords={keywords?.keywords || []}
        authorizations={keywords?.authorizations}
        isLoading={isKeywordsLoading}
        pagination={pagination}
        setPagination={setPagination}
        regions={regions || ({} as Regions)}
        focusKeywordDetail={focusKeywordDetail || ({} as Keyword)}
        isLoadingFocusKeywordDetail={isLoadingFocusKeywordDetail}
        isKeywordsFetching={isKeywordsFetching}
        sorting={sorting}
        onSortingChange={(nextSorting) => setSorting(nextSorting)}
      />
    </div>
  );
};

export default KeywordsPage;

const toApiSorting = (sorting: KeywordColumnSort[]) => {
  return sorting
    .map(({ id, desc }) => `${id} ${desc ? "DESC" : "ASC"}`)
    .join(", ");
};
